import { useCallback, useEffect, useRef, useState } from "react";
import { apiError } from "@/lib/api";
import { backgroundSignal } from "../lib/requestActivity";

/** Only the latest request may publish data; changing inputs clears the previous view. */
export default function useResource(loader, initialData = null) {
  const initial = useRef(initialData);
  const latestLoader = useRef(loader);
  latestLoader.current = loader;
  const mounted = useRef(true);
  const request = useRef({ generation: 0, controller: null });
  const [state, setState] = useState({ data: initial.current, loading: true, error: "", loader });
  const latestState = useRef(state); latestState.current = state;
  const reload = useCallback(async () => {
    if (!mounted.current || latestLoader.current !== loader) return undefined;
    request.current.controller?.abort();
    const controller = new AbortController();
    const generation = ++request.current.generation;
    request.current.controller = controller;
    request.current.kind = "foreground";
    request.current.denied = false;
    setState({ data: initial.current, loading: true, error: "", loader });
    try {
      const data = await loader(controller.signal);
      if (!controller.signal.aborted && generation === request.current.generation) {
        setState({ data, loading: false, error: "", loader });
        return data;
      }
    } catch (error) {
      if (!controller.signal.aborted && generation === request.current.generation) {
        setState({ data: initial.current, loading: false, error: apiError(error), loader });
      }
    } finally {
      if (generation === request.current.generation) request.current.kind = null;
    }
    return undefined;
  }, [loader]);

  // Opt-in background reads retain the current view. Foreground reloads still
  // clear it and supersede any pending read through the same generation guard.
  const background = useRef(null);
  const refresh = useCallback(async (options) => {
    if (!mounted.current || latestLoader.current !== loader || latestState.current.loader !== loader ||
        latestState.current.loading || latestState.current.error || request.current.kind === "foreground" || request.current.denied) return undefined;
    if (background.current?.loader === loader && !background.current.controller.signal.aborted && options?.supersede !== true) return background.current.promise;
    request.current.controller?.abort();
    const controller = new AbortController(), generation = ++request.current.generation;
    request.current.controller = controller;
    request.current.kind = "background";
    const active = () => mounted.current && latestLoader.current === loader && !controller.signal.aborted && generation === request.current.generation;
    setState(previous => previous.loader === loader ? { ...previous, refreshing: true } : previous);
    const task = { loader, controller, promise: null };
    background.current = task;
    task.promise = (async () => {
      try {
        const data = await loader(backgroundSignal(controller.signal));
        if (active()) { setState({ data, loading: false, error: "", refreshError: "", refreshing: false, loader }); return data; }
      } catch (error) {
        if (active()) {
          if ([401, 403, 404].includes(error?.response?.status)) {
            request.current.denied = true;
            setState({ data: initial.current, loading: false, error: apiError(error), refreshError: "", refreshing: false, loader });
          } else {
            setState(previous => previous.loader === loader ? { ...previous, refreshing: false, refreshError: apiError(error) } : previous);
          }
        }
      } finally {
        if (generation === request.current.generation) request.current.kind = null;
        if (background.current === task) background.current = null;
      }
      return undefined;
    })();
    return task.promise;
  }, [loader]);

  useEffect(() => {
    const activeRequest = request.current;
    mounted.current = true;
    reload();
    return () => {
      mounted.current = false;
      activeRequest.controller?.abort();
      activeRequest.generation += 1;
    };
  }, [reload]);

  const current = state.loader === loader ? state : { data: initial.current, loading: true, error: "" };
  return { ...current, reload, refresh };
}
